import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, brl, fmtDateTime, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Plane, FileDown } from "lucide-react";

export default function InvoicePreview() {
  const { id } = useParams();
  const [wo, setWO] = useState(null);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/work-orders/${id}`).then(({ data }) => setWO(data));
    api.get(`/settings/hangar`).then(({ data }) => setSettings(data)).catch(() => setSettings({}));
  }, [id]);

  if (!wo) return <div className="p-8 text-slate-500">Carregando…</div>;

  const subtotal = (wo.services || []).reduce((acc, s) => acc + (Number(s.quantity) || 0) * (Number(s.unit_price) || 0), 0);
  const total = wo.invoice_total ?? subtotal;

  const groundTime = (() => {
    if (!wo.arrival_datetime || !(wo.actual_departure || wo.estimated_departure)) return "—";
    const a = new Date(wo.arrival_datetime);
    const b = new Date(wo.actual_departure || wo.estimated_departure);
    if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return "—";
    const mins = Math.floor((b - a) / 60000);
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${String(h).padStart(2,"0")}h${String(m).padStart(2,"0")}m`;
  })();

  return (
    <div className="bg-slate-100 min-h-screen pb-12">
      <div className="ag-no-print bg-white border-b border-slate-200 sticky top-0 z-10 px-4 py-3 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        <div className="text-xs font-mono text-slate-500">{wo.code} · {wo.invoice_number || "—"}</div>
        <div className="flex gap-2">
          <Button
            onClick={() => window.open(`${API}/work-orders/${wo.id}/invoice.pdf`, "_blank", "noopener")}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            data-testid="download-pdf-button"
          >
            <FileDown className="w-4 h-4 mr-2" /> Baixar PDF (oficial)
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="rounded-sm" data-testid="print-button">
            <Printer className="w-4 h-4 mr-2" /> Imprimir
          </Button>
        </div>
      </div>

      <div className="py-8">
        <div className="ag-a4 text-slate-900" data-testid="invoice-a4">
          {/* Header */}
          <div className="flex justify-between items-start border-b-2 border-slate-900 pb-6">
            <div>
              <div className="flex items-center gap-3">
                {settings?.logo_path ? (
                  <img
                    src={`${API}/files/${settings.logo_path}`}
                    alt={settings.company_name || "Logo"}
                    className="h-12 max-w-[140px] object-contain"
                    data-testid="invoice-header-logo"
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#0052CC] text-white rounded-sm flex items-center justify-center">
                    <Plane className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                )}
                <div>
                  <div className="font-display font-black text-2xl tracking-tight" data-testid="invoice-header-company">
                    {settings?.company_name || "AeroGestor Hangar"}
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                    {settings?.tagline || "Ground Handling & Hangaragem"}
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-600 mt-3 leading-relaxed">
                {(settings?.address_line1 || settings?.address_line2) ? (
                  <>
                    {settings?.address_line1 && <>{settings.address_line1}<br/></>}
                    {settings?.address_line2 && <>{settings.address_line2}<br/></>}
                  </>
                ) : null}
                {[settings?.cnpj && `CNPJ ${settings.cnpj}`, settings?.email, settings?.phone]
                  .filter(Boolean)
                  .join(" · ") || (!settings?.address_line1 ? "Configure os dados em Configurações." : null)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Nota de Serviço</div>
              <div className="font-display font-black text-3xl tracking-tight">{wo.invoice_number || "PRÉ-VIA"}</div>
              <div className="text-xs text-slate-600 mt-1">Emissão: {fmtDate(wo.invoiced_at || new Date().toISOString())}</div>
              <div className="text-xs text-slate-600">OS: <span className="font-mono">{wo.code}</span></div>
            </div>
          </div>

          {/* Client + Aircraft */}
          <div className="grid grid-cols-2 gap-6 mt-6">
            <div>
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500 mb-1">Cliente</div>
              <div className="font-semibold">{wo.client_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500 mb-1">Aeronave</div>
              <div className="font-mono font-semibold uppercase">{wo.tail_number}</div>
              <div className="text-xs text-slate-600">{wo.aircraft_model || "—"} · ANAC {wo.anac_code || "—"}</div>
            </div>
          </div>

          {/* Op metadata */}
          <div className="mt-6 grid grid-cols-4 gap-4 text-xs border border-slate-200 rounded-sm p-3 bg-slate-50">
            <Meta label="Origem" v={wo.flight_origin} />
            <Meta label="Destino" v={wo.flight_destination} />
            <Meta label="Chegada" v={fmtDateTime(wo.arrival_datetime)} />
            <Meta label="Partida" v={fmtDateTime(wo.actual_departure || wo.estimated_departure)} />
            <Meta label="Comandante" v={wo.commander_name} />
            <Meta label="ANAC Piloto" v={wo.pilot_anac_code} />
            <Meta label="Pax" v={String(wo.passenger_count || 0)} />
            <Meta label="Permanência" v={groundTime} mono />
          </div>

          {/* Line items */}
          <div className="mt-6">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500 mb-2">Serviços</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-900 text-[10px] uppercase tracking-wider text-slate-700">
                  <th className="text-left py-2">Descrição</th>
                  <th className="text-right py-2 w-20">Qtd</th>
                  <th className="text-right py-2 w-32">Unit.</th>
                  <th className="text-right py-2 w-32">Total</th>
                </tr>
              </thead>
              <tbody>
                {(wo.services || []).map((s) => (
                  <tr key={s.id} className="border-b border-slate-100">
                    <td className="py-2">{s.name}</td>
                    <td className="py-2 text-right font-mono tabular-nums">{s.quantity}</td>
                    <td className="py-2 text-right font-mono tabular-nums">{brl(s.unit_price)}</td>
                    <td className="py-2 text-right font-mono tabular-nums">{brl((s.quantity || 0) * (s.unit_price || 0))}</td>
                  </tr>
                ))}
                {(wo.services || []).length === 0 && (
                  <tr><td colSpan={4} className="py-4 text-center text-slate-400 text-xs">Nenhum serviço.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-4 flex justify-end">
            <div className="w-72">
              <div className="flex justify-between py-1 text-sm text-slate-700">
                <span>Subtotal</span><span className="font-mono">{brl(subtotal)}</span>
              </div>
              <div className="flex justify-between py-3 mt-2 border-t-2 border-slate-900">
                <span className="font-display font-black text-lg">Total</span>
                <span className="font-display font-black text-lg font-mono" data-testid="invoice-total">{brl(total)}</span>
              </div>
            </div>
          </div>

          {/* Signatures */}
          {(wo.client_signature || wo.operator_signature) && (
            <div className="mt-10 grid grid-cols-2 gap-6 pt-6 border-t border-slate-200">
              <SigBlock label="Cliente / Responsável" src={wo.client_signature} />
              <SigBlock label="Operador" src={wo.operator_signature} />
            </div>
          )}

          {wo.closing_remarks && (
            <div className="mt-6 text-xs text-slate-600">
              <div className="font-bold uppercase tracking-wider text-slate-500 mb-1">Observações</div>
              {wo.closing_remarks}
            </div>
          )}

          {/* Photo Evidence Gallery */}
          {((wo.checklist?.fotos_intake?.length || 0) > 0 || (wo.final_photos?.length || 0) > 0) && (
            <div className="mt-10 pt-6 border-t border-slate-200" data-testid="invoice-photo-evidence">
              <div className="font-bold uppercase tracking-[0.18em] text-slate-500 text-[10px] mb-3">Evidência Fotográfica</div>
              {(wo.checklist?.fotos_intake?.length || 0) > 0 && (
                <div className="mb-5">
                  <div className="text-[9px] font-bold tracking-[0.15em] uppercase text-slate-500 mb-2">Intake (Chegada)</div>
                  <PhotoGrid paths={wo.checklist.fotos_intake} />
                </div>
              )}
              {(wo.final_photos?.length || 0) > 0 && (
                <div>
                  <div className="text-[9px] font-bold tracking-[0.15em] uppercase text-slate-500 mb-2">Finalização (Entrega)</div>
                  <PhotoGrid paths={wo.final_photos} />
                </div>
              )}
            </div>
          )}

          <div className="mt-12 text-center text-[10px] tracking-[0.2em] uppercase text-slate-400">
            Documento gerado eletronicamente · AeroGestor
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, v, mono }) {
  return (
    <div>
      <div className="font-bold tracking-[0.15em] uppercase text-slate-500 text-[9px]">{label}</div>
      <div className={mono ? "font-mono mt-0.5" : "mt-0.5"}>{v || "—"}</div>
    </div>
  );
}
function SigBlock({ label, src }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">{label}</div>
      <div className="h-24 border-b border-slate-300 flex items-end justify-center pb-1 mt-2">
        {src ? <img src={src} alt={label} className="max-h-24 object-contain" /> : <span className="text-slate-300 text-xs">—</span>}
      </div>
    </div>
  );
}

function PhotoGrid({ paths }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {paths.slice(0, 12).map((p, i) => (
        <div key={p + i} className="aspect-[4/3] border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
          <img
            src={`${API}/files/${p}`}
            alt={`Evidência ${i + 1}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      ))}
    </div>
  );
}
